import React, { useContext, useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  Paper,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  styled
} from '@mui/material';
import {
  ArrowRight as ArrowRightIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Refresh as RefreshIcon,
  Code as CodeIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import CustomScrollbar from '../../TestPage/components/CustomScrollbar';
import { ColorContext } from '../../../AppTheme';

// Styled components
const SourceTreeContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden'
}));

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper
}));

const ElementTag = styled(Box)(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  backgroundColor: selected ? theme.palette.primary.light : 'transparent',
  color: selected ? theme.palette.primary.contrastText : 'inherit',
  '&:hover': {
    backgroundColor: selected ? theme.palette.primary.main : theme.palette.action.hover
  },
  transition: theme.transitions.create(['background-color'], {
    duration: theme.transitions.duration.shortest
  })
}));

const PropertyGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(2),
  '& .MuiGrid-item': {
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`
  }
}));

const PropertyHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  fontSize: '0.75rem'
}));

const PropertyValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  wordBreak: 'break-word'
}));

const TreeItem = ({ source, depth = 0, selectedElement, setSelectedElement }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isSelected = source === selectedElement;

  const handleToggle = () => {
    setExpanded(!expanded);
    setSelectedElement(source);
  };

  return (
    <Box sx={{ pl: depth * 1.5 }}>
      <ElementTag selected={isSelected} onClick={handleToggle}>
        {expanded ? <ArrowDropDownIcon fontSize="small" /> : <ArrowRightIcon fontSize="small" />}
        <Typography variant="body2" ml={1} noWrap>
          &lt;{source?.attributes?.class || source?.type || 'element'}&gt;
        </Typography>
        {source?.children?.length > 0 && (
          <Chip label={source.children.length} size="small" sx={{ ml: 'auto', mr: 1 }} />
        )}
      </ElementTag>

      {expanded &&
        source?.children?.map((child, i) => (
          <TreeItem
            key={i}
            source={child}
            depth={depth + 1}
            selectedElement={selectedElement}
            setSelectedElement={setSelectedElement}
          />
        ))}
    </Box>
  );
};

const ElementInspector = ({ selectedElement }) => {
  const excludedKeys = ['children', 'bounds', 'attributes'];
  const theme = useTheme();

  if (!selectedElement?.attributes) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: theme.palette.text.secondary
        }}>
        <CodeIcon fontSize="large" />
        <Typography variant="body1" mt={2}>
          Select an element to inspect
        </Typography>
      </Box>
    );
  }

  return (
    <CustomScrollbar sx={{ height: '100%', overflowY: 'auto' }}>
      <Box p={2}>
        <Accordion defaultExpanded elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight={500}>
              Element Properties
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <PropertyGrid container>
              {Object.entries(selectedElement).map(([key, value]) => (
                <React.Fragment key={key}>
                  {!excludedKeys.includes(key) && (
                    <>
                      <Grid item xs={6}>
                        <PropertyHeader>{key}</PropertyHeader>
                      </Grid>
                      <Grid item xs={6}>
                        <PropertyValue>
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </PropertyValue>
                      </Grid>
                    </>
                  )}
                </React.Fragment>
              ))}
            </PropertyGrid>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded elevation={0} sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight={500}>
              Element Attributes
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <PropertyGrid container>
              {Object.entries(selectedElement?.attributes).map(([key, value]) => (
                <React.Fragment key={key}>
                  <Grid item xs={6}>
                    <PropertyHeader>{key}</PropertyHeader>
                  </Grid>
                  <Grid item xs={6}>
                    <PropertyValue>{value}</PropertyValue>
                  </Grid>
                </React.Fragment>
              ))}
            </PropertyGrid>
          </AccordionDetails>
        </Accordion>
      </Box>
    </CustomScrollbar>
  );
};

const AppSource = ({
  appSource,
  selectedElement,
  setSelectedElement,
  sessionActive,
  handleInspectToggle
}) => {
  const { mode } = useContext(ColorContext);
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        gap: 2,
        flexDirection: { xs: 'column', md: 'row' }
      }}>
      {/* Source Tree Panel */}
      <SourceTreeContainer>
        <Header>
          <Typography variant="subtitle1" fontWeight={600}>
            App Source Tree
          </Typography>
          <Tooltip title="Refresh source tree">
            <IconButton
              size="small"
              disabled={!sessionActive}
              onClick={handleInspectToggle}
              sx={{
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Header>

        <CustomScrollbar sx={{ flex: 1, p: 1 }}>
          {appSource?.length ? (
            appSource.map((source, i) => (
              <TreeItem
                key={i}
                source={source}
                selectedElement={selectedElement}
                setSelectedElement={setSelectedElement}
              />
            ))
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: theme.palette.text.secondary
              }}>
              <Typography variant="body1">No source elements available</Typography>
            </Box>
          )}
        </CustomScrollbar>
      </SourceTreeContainer>

      {/* Element Inspector Panel */}
      <SourceTreeContainer>
        <Header>
          <Typography variant="subtitle1" fontWeight={600}>
            Element Inspector
          </Typography>
          {selectedElement?.attributes && (
            <Chip label="Selected" size="small" color="primary" variant="outlined" />
          )}
        </Header>

        <ElementInspector selectedElement={selectedElement} />
      </SourceTreeContainer>
    </Box>
  );
};

export default AppSource;
